// Shared between CampaignEditForm's Comments sheet and EditRequestReviewPanel
// so both surfaces label + format the same dot-paths identically.

import { fmtDeliverableType } from '@discovr/types';
import type { FieldChange, ProductInput } from '@discovr/types';
import { formatMoneyExact, formatTierRange } from '../../lib/format';
import { formatMoney } from '../../lib/formatMoney';
import { titleCase, formatRegion } from './campaignDetailsParts';
import { PRIMARY_GOAL_OPTIONS, PAYOUT_MODEL_OPTIONS } from './editFieldOptions';
import { OFFICIAL_AUDIO_SONG_FALLBACK } from './officialAudioDisplay';

// Dot-path for the products field.
export const PRODUCTS_FIELD_PATH = 'products';

export const FIELD_LABELS: Record<string, string> = {
  name: 'Campaign name',
  primary_goal: 'Primary goal',
  total_budget: 'Total budget',
  description: 'Description',
  deliverables_text: 'Deliverables description',
  deliverables: 'Content types / add-ons',
  usage_rights: 'Usage rights',
  official_audio: 'Campaign audio',
  payout_model: 'Payout model',
  'creator_preference.creator_count': 'Creator count',
  'creator_preference.categories': 'Categories',
  'creator_preference.tiers': 'Creator tiers',
  'creator_preference.creator_genders': 'Creator gender',
  'creator_preference.gender_notes': 'Creator gender notes',
  'targeting.demographics.age_min': 'Age (min)',
  'targeting.demographics.age_max': 'Age (max)',
  'targeting.demographics.genders': 'Audience gender',
  'targeting.demographics.languages': 'Language',
  'targeting.demographics.regions': 'Region',
  'brief.primary_focus': 'Primary focus',
  'brief.secondary_focus': 'Secondary focus',
  'brief.video_title': 'Brief title',
  'brief.video_description': 'Brief description',
  'brief.cta': 'Call to action',
  'brief.dos': "Do's",
  'brief.donts': "Don'ts",
  'brief.sample_video_url': 'Reference video',
  [PRODUCTS_FIELD_PATH]: 'Products',
};

export function formatFieldValue(v: unknown): string {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') {
    const days = (v as { days?: number }).days;
    // != null, not truthy — a legitimate `{days: 0}` must not fall through
    // to raw JSON.
    return days != null ? `${days} days` : JSON.stringify(v);
  }
  return String(v);
}

// Route each path through the same formatter its card/field uses, so a diff shows
// formatted values, not raw numbers.
const TITLE_CASED_PATHS = new Set([
  'targeting.demographics.genders',
  'targeting.demographics.languages',
  'targeting.demographics.regions',
  'creator_preference.creator_genders',
  'creator_preference.categories',
]);

export function formatScalar(path: string, v: unknown, currency?: string | null): string {
  switch (path) {
    case 'total_budget':
      return currency && currency.toUpperCase() !== 'INR'
        ? formatMoney({ amount: Number(v), currency })
        : formatMoneyExact(Number(v));
    case 'creator_preference.tiers':
      return formatTierRange(String(v));
    case 'targeting.demographics.regions':
      return formatRegion(String(v));
    case 'deliverables':
      return fmtDeliverableType(String(v));
    case 'primary_goal':
      return PRIMARY_GOAL_OPTIONS.find((o) => o.value === v)?.label ?? String(v);
    case 'payout_model':
      return PAYOUT_MODEL_OPTIONS.find((o) => o.value === v)?.label ?? String(v);
    default:
      return TITLE_CASED_PATHS.has(path) ? titleCase(String(v)) : String(v);
  }
}

// Coerces an unknown FieldChange value into a products array — non-array
// values (missing, null, legacy/corrupt data) become an empty catalog.
export function asProductArray(v: unknown): ProductInput[] {
  return Array.isArray(v) ? (v as ProductInput[]) : [];
}

// Count of a products array value — 0 for anything not an array.
function productCount(v: unknown): number {
  return asProductArray(v).length;
}

// "2 products" / "1 product" — products are name/link/note records, not
// scalars, so they get a count summary instead of a joined string.
function formatProductCount(n: number): string {
  return `${n} product${n === 1 ? '' : 's'}`;
}

// Summarises an official_audio value as its track name (or a count when the
// name wasn't resolved), so a review diff reads "Sajni – Laapataa Ladies", not
// raw JSON. "—" when there is no audio (the "before" side of an add).
function formatOfficialAudio(v: unknown): string {
  const audio = v as { audio_ids?: string[]; tracks?: Array<{ song_name?: string }> } | null;
  const ids = audio?.audio_ids ?? [];
  if (ids.length === 0) return '—';
  const song = audio?.tracks?.[0]?.song_name?.trim();
  if (ids.length > 1) return `${ids.length} audios`;
  return song || OFFICIAL_AUDIO_SONG_FALLBACK;
}

// Path-aware sibling of formatFieldValue. Products get a count-based summary
// here; the rich before/after render lives in ProductListReadonly instead.
export function formatFieldValueFor(path: string, v: unknown, currency?: string | null): string {
  if (path === PRODUCTS_FIELD_PATH) {
    const count = productCount(v);
    return count === 0 ? '—' : formatProductCount(count);
  }
  if (path === 'official_audio') return formatOfficialAudio(v);
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map((x) => formatScalar(path, x, currency)).join(', ') : '—';
  if (typeof v === 'object') return formatFieldValue(v);
  return formatScalar(path, v, currency);
}

export type ChangeKind = 'added' | 'removed' | 'changed';

export interface ChangeSummary {
  kind: ChangeKind;
  /** Only set for `changed` — the struck-through previous value. */
  before?: string;
  after: string;
}

const isEmpty = (v: unknown) => v == null || v === '' || (Array.isArray(v) && v.length === 0);

/** The displayed change type, classified element-wise: arrays with only
 *  additions are "added", only removals "removed", a mix "changed"; scalars go
 *  by empty-before/after. Can differ from the BE's empty-based change_type
 *  (dropping one of several values is "removed" here, "changed" there). */
export function diffKind(before: unknown, after: unknown): ChangeKind {
  if (Array.isArray(before) && Array.isArray(after)) {
    const added = after.filter((x) => !before.includes(x));
    const removed = before.filter((x) => !after.includes(x));
    if (added.length && !removed.length) return 'added';
    if (removed.length && !added.length) return 'removed';
    return 'changed';
  }
  if (isEmpty(before)) return 'added';
  if (isEmpty(after)) return 'removed';
  return 'changed';
}

// Products before/after as a count summary ("2 -> 3 products") — the generic
// array diff below uses reference equality, which is wrong for rebuilt objects.
function describeProductsChange(before: unknown, after: unknown): ChangeSummary {
  const beforeCount = productCount(before);
  const afterCount = productCount(after);
  if (beforeCount === 0) return { kind: 'added', after: formatProductCount(afterCount) };
  if (afterCount === 0) return { kind: 'removed', after: formatProductCount(beforeCount) };
  return { kind: 'changed', before: formatProductCount(beforeCount), after: formatProductCount(afterCount) };
}

/**
 * Classifies an edit as added / removed / changed and renders the Comments
 * card's one-line summary. Multi-value fields diff element-wise, so dropping
 * one language off four reads "Removed → Tamil" rather than restating both lists.
 */
export function describeChange(path: string, before: unknown, after: unknown, currency?: string | null): ChangeSummary {
  if (path === PRODUCTS_FIELD_PATH) return describeProductsChange(before, after);
  if (Array.isArray(before) && Array.isArray(after)) {
    const added = after.filter((x) => !before.includes(x));
    const removed = before.filter((x) => !after.includes(x));
    if (added.length && !removed.length) return { kind: 'added', after: formatFieldValueFor(path, added, currency) };
    if (removed.length && !added.length) return { kind: 'removed', after: formatFieldValueFor(path, removed, currency) };
  }
  if (isEmpty(before)) return { kind: 'added', after: formatFieldValueFor(path, after, currency) };
  if (isEmpty(after)) return { kind: 'removed', after: formatFieldValueFor(path, before, currency) };
  return { kind: 'changed', before: formatFieldValueFor(path, before, currency), after: formatFieldValueFor(path, after, currency) };
}

/**
 * Scrolls to a `field-<dot-path>` anchor and flashes a ring around the whole
 * field group. Rings the nearest `.min-w-0` container (falling back to the
 * anchor) so the highlight frames the whole field, not just a marker.
 */
const HIGHLIGHT_CLASSES = [
  'ring-2',
  'ring-foreground',
  // Offset pushes the ring out so the box reads as one padded frame.
  'ring-offset-8',
  'ring-offset-background',
  'rounded-lg',
];

export function goToField(path: string) {
  const anchor = document.getElementById(`field-${path}`);
  if (!anchor) return;
  const target = anchor.closest<HTMLElement>('.min-w-0') ?? anchor;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target.classList.add(...HIGHLIGHT_CLASSES);
  window.setTimeout(() => target.classList.remove(...HIGHLIGHT_CLASSES), 1600);
}

/** Slightly over the Sheet's 300ms close transition — wait it out before
 *  scrolling so the field isn't hidden behind a mid-close panel. */
export const SHEET_CLOSE_ANIMATION_MS = 320;

/** Coarse "Nh ago" / "Nd ago" — no i18n, matches the review panel's own
 *  timestamp style. Falls back to a locale date once it's over a week old. */
export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Chat-app-style comment timestamp: same calendar day → time only ("3:45
 *  PM"); same calendar year (but a different day) → "DD-MMM" ("15-Aug"); an
 *  older year → "DD-MMM-YYYY". No i18n, matches formatRelativeTime's scope. */
export function formatCommentTimestamp(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  if (then.toDateString() === now.toDateString()) {
    return then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  const day = String(then.getDate()).padStart(2, '0');
  const month = then.toLocaleDateString(undefined, { month: 'short' });
  if (then.getFullYear() === now.getFullYear()) {
    return `${day}-${month}`;
  }
  return `${day}-${month}-${then.getFullYear()}`;
}

/** "brand_owner" → "Brand manager", "admin_member" → "Admin", etc. — no
 *  display-label helper exists on the BE Role type, so this stays FE-only. */
export function formatRoleLabel(role: string | undefined): string {
  switch (role) {
    case 'brand_owner':
    case 'brand_admin':
    case 'brand_member':
      return 'Brand manager';
    case 'super_admin':
    case 'admin':
    case 'admin_member':
      return 'Admin';
    case 'creator':
      return 'Creator';
    case 'talent_manager':
      return 'Talent manager';
    default:
      return '';
  }
}

/** True for any admin-tier role (`admin`, `admin_member`, `super_admin`).
 *  Admin edits are self-approved in the same transaction, so their comment
 *  thread stays open even after approval. */
export function isAdminRole(role: string | undefined): boolean {
  return role === 'admin' || role === 'admin_member' || role === 'super_admin';
}

/** True when `change` was approved without a real review — admin edits are
 *  self-approved in the same BE transaction. Callers swap in a distinct
 *  "Applied by admin" label so it doesn't read like a reviewed approval. */
export function isSelfApprovedByAdmin(change: Pick<FieldChange, 'status' | 'submitted_by_role'>): boolean {
  return change.status === 'approved' && isAdminRole(change.submitted_by_role);
}

/** One-line summary of who's behind a batch of changes. A single request can
 *  mix admin's direct edits with a brand's request, and the request-level
 *  `submitted_by` only names whoever opened it — so this reads each
 *  FieldChange's own `submitted_by_role`/`submitted_by_name` to avoid
 *  misattributing fields. */
export function describeRequestSubmitters(changes: FieldChange[]): string {
  if (changes.length === 0) return '';
  const adminCount = changes.filter((c) => isAdminRole(c.submitted_by_role)).length;
  const otherChanges = changes.filter((c) => !isAdminRole(c.submitted_by_role));
  const otherNames = Array.from(new Set(otherChanges.map((c) => c.submitted_by_name).filter((n): n is string => Boolean(n))));

  if (adminCount === 0) {
    return otherNames.length <= 1 ? `Submitted by ${otherNames[0] ?? 'the brand'}` : `Submitted by ${otherNames.join(', ')}`;
  }
  if (otherChanges.length === 0) return 'Applied directly by admin';

  const brandLabel = otherNames.length <= 1 ? (otherNames[0] ?? 'the brand') : `${otherNames.length} people`;
  return `${adminCount} applied by admin, ${otherChanges.length} requested by ${brandLabel}`;
}
